import React, { useEffect, useState } from 'react';
import { Box, Stack, Tab, Tabs } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import usePurchaseInvoiceRegisterListFilters from '../hooks/usePurchaseInvoiceRegisterListFilters';
import PurchaseInvoiceRegisterListFilters from '../components/PurchaseInvoiceRegisterListFilters';
import TopBar from '../../../components/TopBar';
import FeatureNotSupportedDialog from '../../common/FeatureNotSupportedDialog';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import PurchaseInvoiceRegisterTable from '../components/PurchaseInvoiceRegisterTable';
import PurchaseInvoiceRegisterListActions from '../components/PurchaseInvoiceRegisterListActions';
import PurchaseCreditNoteRegisterTable from '../components/PurchaseCreditNoteRegisterTable';
import PurchaseCreditNoteRegisterListActions from '../components/PurchaseCreditNoteRegisterListActions';

const TAB_KEYS = ['invoices', 'credit-notes'];

function PurchaseInvoiceRegisterListPage() {
  const { purchaseInvoiceRegister, eInvoiceForm, eInvoiceListPage } = useTranslatedMessages();
  useDocumentTitle(purchaseInvoiceRegister?.pages?.document_title ?? 'Purchase Invoice Register');
  const navigate = useNavigate();
  const location = useLocation();
  const subscription = useSelector((state) => state.subscription.subscription);
  const isRegisterFeatureEnabled = subscription?.features?.enable_register;

  const tabs = [
    { key: 'invoices', label: eInvoiceForm.document_list.tab_invoices },
    { key: 'credit-notes', label: eInvoiceForm.document_list.tab_credit_notes },
  ];

  const queryParams = new URLSearchParams(location.search);
  const tabFromUrl = queryParams.get('tab');
  const initialTab = TAB_KEYS.includes(tabFromUrl) ? tabFromUrl : 'invoices';
  const [selectedTab, setSelectedTab] = useState(initialTab);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    navigate(`/purchase-invoice-register?tab=${newValue}`);
  };

  const getTopBarTitle = (tab) => {
    const pages = purchaseInvoiceRegister?.pages ?? {};
    switch (tab) {
      case 'invoices':
        return pages.list_title_invoices ?? pages.list_title ?? 'Purchase Invoice Register';
      case 'credit-notes':
        return pages.list_title_credit_notes ?? 'Purchase Credit Note Register';
      default:
        return pages.list_title ?? 'Purchase Invoice Register';
    }
  };

  useEffect(() => {
    setSelectedTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    if (tabFromUrl && !TAB_KEYS.includes(tabFromUrl)) {
      navigate('/purchase-invoice-register?tab=invoices', { replace: true });
    }
  }, [tabFromUrl, navigate]);

  const filters = [
    {
      type: 'search',
      options: [
        {
          key: 'document_number',
          placeholder:
            selectedTab === 'credit-notes'
              ? (eInvoiceListPage?.filters?.credit_note_number_label ?? 'Credit Note Number')
              : (eInvoiceListPage?.filters?.invoice_number_label ?? 'Invoice Number'),
        },
        { key: 'billed_to_name', placeholder: eInvoiceListPage?.filters?.billed_to_name ?? 'Buyer Name' },
        { key: 'billed_to_vat', placeholder: eInvoiceListPage?.filters?.billed_to_vat ?? 'Buyer VAT' },
        { key: 'billed_by_name', placeholder: eInvoiceListPage?.filters?.billed_by_name ?? 'Seller Name' },
        { key: 'billed_by_vat', placeholder: eInvoiceListPage?.filters?.billed_by_vat ?? 'Seller VAT' },
      ],
    },
  ];

  const {
    selectedFilter,
    searchTerm,
    currentQuery,
    dateRange,
    documentModeFilter,
    handleFilterChange,
    handleSearchChange,
    handleSearchKeyPress,
    handleDateRangeChange,
    handleDocumentModeFilterChange,
    handlePaginationChange,
  } = usePurchaseInvoiceRegisterListFilters({
    searchOnChange: true,
    persistKey: `purchase-invoice-register/list:${selectedTab}`,
  });

  if (!isRegisterFeatureEnabled) {
    return (
      <FeatureNotSupportedDialog
        title={purchaseInvoiceRegister?.feature_not_supported?.title ?? 'Purchase Invoice Register'}
        description={
          purchaseInvoiceRegister?.feature_not_supported?.description ??
          'Register allows you to track transactions that are currently not captured as e-invoices.'
        }
      />
    );
  }

  return (
    <Stack direction="column" spacing={3} sx={{ mt: '-1rem' }}>
      <TopBar title={getTopBarTitle(selectedTab)} />
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Tabs value={selectedTab} onChange={handleTabChange}>
          {tabs.map((tab) => (
            <Tab key={tab.key} label={tab.label} value={tab.key} />
          ))}
        </Tabs>
        {selectedTab === 'invoices' && <PurchaseInvoiceRegisterListActions />}
        {selectedTab === 'credit-notes' && <PurchaseCreditNoteRegisterListActions />}
      </Box>

      {selectedTab === 'invoices' ? (
        <>
          <Box mb={2}>
            <PurchaseInvoiceRegisterListFilters
              filters={filters}
              selectedFilter={selectedFilter}
              onFilterChange={handleFilterChange}
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
              onSearchKeyPress={handleSearchKeyPress}
              dateRange={dateRange}
              onDateRangeChange={handleDateRangeChange}
              documentModeFilter={documentModeFilter}
              onDocumentModeFilterChange={handleDocumentModeFilterChange}
            />
          </Box>
          <PurchaseInvoiceRegisterTable handlePaginationChange={handlePaginationChange} searchQuery={currentQuery} />
        </>
      ) : (
        <>
          <Box mb={2}>
            <PurchaseInvoiceRegisterListFilters
              filters={filters}
              selectedFilter={selectedFilter}
              onFilterChange={handleFilterChange}
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
              onSearchKeyPress={handleSearchKeyPress}
              dateRange={dateRange}
              onDateRangeChange={handleDateRangeChange}
              documentModeFilter={documentModeFilter}
              onDocumentModeFilterChange={handleDocumentModeFilterChange}
            />
          </Box>
          <PurchaseCreditNoteRegisterTable handlePaginationChange={handlePaginationChange} searchQuery={currentQuery} />
        </>
      )}
    </Stack>
  );
}

export default PurchaseInvoiceRegisterListPage;
